import React, { useMemo } from 'react';
import { RawHtml } from '~/shared/components';
import { SummaryCategories } from '~/shared/components/Summary/components/SummaryCategories';
import { SummarySpecifications } from '~/shared/components/Summary/components/SummarySpecifications';
import { Link } from '~/lib/data-contract/auto-generated/api-model';
import { Product } from '~/lib/data-contract/salesforce';
import { P50ProductDetailsPage } from '../../pdp.definitions';
import { getSpecs, getStorePersonnelSpecs } from '../../pdp-utils';
import { useTranslation } from '~/shared/utils/translation';
import { useCustomerStore } from '~/features/commerce-api/hooks/useCustomerStore';
import { useEvents } from '~/shared/hooks/useEvents';
import { useFrame } from '~/shared/utils';
import { ProductDocumentation } from '~/shared/components/Summary/components/ProductDocumentation';
import { MatchingProducts } from '../MatchingProducts';
import { breakpoints } from '~/theme/breakpoints';
import styled from '@emotion/styled';
import Accordion from '~/shared/components/Summary/components/Accordion';

type Props = {
    product: Product;
    freightInfo?: { text?: string; title?: string };
    variant?: { productId: string };
    page: P50ProductDetailsPage;
};

const StyledMatchingProductsWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    marginTop: theme.space['4'],
    [breakpoints.md]: {
        display: 'none',
    },
}));

const getCategories = (page: P50ProductDetailsPage) => {
    const categoriesAsArray = Object.values(page?.productCategoryLinks).filter((link) => link);
    return {
        categoriesAsArray: categoriesAsArray.length > 0 ? (categoriesAsArray as Link[]) : null,
        productCategoryLinks: page.productCategoryLinks,
    };
};

export const ProductDetailSections = ({ product, freightInfo, variant, page }: Props) => {
    const { translate } = useTranslation();
    const { isStorePersonnel } = useCustomerStore();
    const { data: frame } = useFrame();
    const { PDPActionEvent } = useEvents(frame);

    const productDescription = useMemo(() => {
        const usps = (product?.c_ProductUsp || '').split(/\n/g);

        const uspList = product?.c_ProductUsp
            ? `<ul style="margin: 0; display: inline-block;">${usps.map((item) => `<li>${item}</li>`).join('')}</ul>`
            : '';

        return [product?.shortDescription, uspList, product?.longDescription]
            .filter((desc) => !!desc)
            .join('\n\n');
    }, [product]);

    const { categoriesAsArray, productCategoryLinks } = getCategories(
        page as P50ProductDetailsPage,
    );

    const specs = {
        categories: productCategoryLinks,
        product: product,
    };

    const productSpecs = isStorePersonnel ? getStorePersonnelSpecs(specs) : getSpecs(specs);
    const productDocumentation = product?.c_documentationLinks
        ? JSON.parse(product?.c_documentationLinks)
        : null;

    const onToggleAccordion = (isOpen: boolean, accordion: string) => {
        if (!product) return;

        PDPActionEvent(
            `${isOpen ? 'Open' : 'Close'} Accordion - ${accordion}`,
            `${variant?.productId} | ${product.name}`,
        );
    };

    return (
        <>
            {productDescription && (
                <Accordion
                    title={translate('pdp.summary.descriptionTitle')}
                    isOpen
                    onToggle={onToggleAccordion}
                >
                    <RawHtml html={productDescription} shouldSanitizeHtml />
                </Accordion>
            )}

            {productSpecs && (
                <Accordion
                    title={translate('pdp.summary.specsTitle')}
                    initOpenForSEO
                    onToggle={onToggleAccordion}
                >
                    <SummarySpecifications specifications={productSpecs} />
                </Accordion>
            )}

            {freightInfo?.text && freightInfo?.title && (
                <Accordion title={freightInfo.title} initOpenForSEO onToggle={onToggleAccordion}>
                    <RawHtml html={freightInfo.text} />
                </Accordion>
            )}

            {productDocumentation && (
                <Accordion
                    title={translate('pdp.summary.documentationTitle')}
                    initOpenForSEO
                    onToggle={onToggleAccordion}
                >
                    <ProductDocumentation productDocumentation={productDocumentation} />
                </Accordion>
            )}
            {categoriesAsArray && (
                <>
                    <SummaryCategories
                        title={translate('pdp.summary.categoriesTitle')}
                        categories={categoriesAsArray}
                        onToggle={(category: string) =>
                            PDPActionEvent(
                                `Category Click - ${category}`,
                                `${variant?.productId} | ${product.name}`,
                            )
                        }
                    />
                    <StyledMatchingProductsWrapper>
                        <MatchingProducts />
                    </StyledMatchingProductsWrapper>
                </>
            )}
        </>
    );
};
